import React, { useCallback } from 'react'

import { useApolloClient } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  FormsTypeEnum,
  MySurveysDataCollectionDocument,
  MySurveysDataCollectionQuery,
  MySurveysMembershipsFilterDocument,
  MySurveysTargetFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'
import { translateFormType } from 'modules/trainings/lib/trainingFormTypes'

export const MySurveysDataCollection = ({
  onFormSelect,
  onRefetch,
}: {
  onFormSelect: (form: {
    formId: string
    resourceId: string
    membershipId: string
    formType: string
    materializedFormId: string
    hasPreviousAnswers: boolean
    respondentId?: string | undefined
  }) => void
  onRefetch?: (refetch: () => void) => void
}) => {
  const { access } = useCurrentSession()
  const client = useApolloClient()

  const refetch = useCallback(() => {
    client.refetchQueries({
      include: [MySurveysDataCollectionDocument],
    })
  }, [client])

  React.useEffect(() => {
    if (onRefetch) {
      onRefetch(refetch)
    }
  }, [onRefetch, refetch])

  const source = useGraphqlDataCollectionSource({
    document: MySurveysDataCollectionDocument,
    mapData: (data) => data.forms.membershipsConnection.nodes,
    mapPagination: (data) => {
      return {
        total: data.forms.membershipsConnection.nodes.length ?? 0,
      }
    },
    mapVariables: ({ filters, search, sortings }) => {
      const formType = filters.formType || ['satisfaction', 'effectiveness', 'knowledge']
      return {
        respondentId: access.id.toString(),
        search,
        formType,
        sort: sortings?.[0],
      }
    },
    itemOnClick:
      (
        item: NonNullable<
          MySurveysDataCollectionQuery['forms']['membershipsConnection']['nodes']
        >[0]
      ) =>
      () => {
        if (item.materializedForm) {
          onFormSelect({
            formId: item.materializedForm?.form?.id ?? '',
            resourceId: item.materializedForm?.resourceId ?? '',
            membershipId: item.id,
            formType: item.materializedForm?.formType ?? '',
            materializedFormId: item.materializedForm?.id ?? '',
            hasPreviousAnswers: (item.answersConnection?.totalCount ?? 0) > 0,
            respondentId: item?.respondentId?.toString(),
          })
        }
      },
    filters: {
      formType: {
        label: i18n.t('trainings.my_surveys.datatable.filters.type'),
        type: 'in',
        options: {
          options: [
            {
              label: translateFormType('satisfaction'),
              value: 'satisfaction',
            },
            {
              label: translateFormType('effectiveness'),
              value: 'effectiveness',
            },
            {
              label: translateFormType('knowledge'),
              value: 'knowledge',
            },
          ],
        },
      },
      participant: {
        label: i18n.t('meetings.all.table.participants'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: MySurveysMembershipsFilterDocument,
              fetchPolicy: 'cache-first',
              variables: {
                respondentId: access.id.toString(),
              },
            })

            const uniqueArray = result.data?.forms.membershipsConnection.nodes.reduce(
              (acc: MySurveysTargetFragment[], { target }) => {
                if (target && !acc.includes(target)) acc.push(target)
                return acc
              },
              []
            )

            return (
              uniqueArray.map((target) => ({
                label: target?.employee?.fullName || '',
                value: target?.employee?.id,
              })) || []
            )
          },
        },
      },
    },
    sortings: {
      status: {
        label: i18n.t('trainings.my_surveys.datatable.status.label'),
      },
    },
    defaultSortings: {
      field: 'status',
      order: 'asc',
    },
    search: {
      enabled: true,
    },
  })

  return (
    <>
      <OneDataCollection
        id="trainings/my_surveys/v1"
        storage={DEFAULT_DATA_COLLECTION_STORAGE}
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('trainings.my_surveys.datatable.name'),
                  render: (item) => ({
                    type: 'text',
                    value: item.materializedForm?.form?.title,
                  }),
                },
                {
                  label: i18n.t('trainings.my_surveys.datatable.status.label'),
                  render: (item) => ({
                    type: 'status',
                    value: {
                      label:
                        item.status === 'completed'
                          ? i18n.t('trainings.my_surveys.datatable.status.completed')
                          : i18n.t('trainings.my_surveys.datatable.status.pending'),
                      status: item.status === 'completed' ? 'positive' : 'neutral',
                    },
                  }),
                  sorting: 'status',
                },
                {
                  label: i18n.t('trainings.my_surveys.datatable.course'),
                  // sorting: 'materializedForm.resourceName',
                  render: (item) => ({
                    type: 'text',
                    value: item.materializedForm?.resourceFormName,
                  }),
                },
                {
                  label: i18n.t('trainings.my_surveys.datatable.participant'),
                  render: (item) => ({
                    type: 'person',
                    value: {
                      firstName: item.target?.firstName,
                      lastName: item.target?.lastName,
                      avatar: item.target?.avatar,
                    },
                  }),
                },
                {
                  label: 'Form type',
                  render: (item) => {
                    const formType = item.materializedForm?.formType
                    const validFormType: FormsTypeEnum =
                      formType === 'satisfaction' ||
                      formType === 'effectiveness' ||
                      formType === 'knowledge' ||
                      formType === 'custom'
                        ? formType
                        : 'satisfaction'
                    return {
                      type: 'text',
                      value: translateFormType(validFormType),
                    }
                  },
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
    </>
  )
}
